import { Button } from "@/components/flexkit/Button";
import { Card } from "@/components/flexkit/Card";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { Pill } from "@/components/podkit/status/Pill";
import { Text } from "@/components/podkit/typography/Text";
import { formatShortDate } from "@/format/time";
import { useSegmentTrack } from "@/hooks/use-segment";
import {
    useGetBillingInfo,
    useListSubscriptions,
    useOrganization,
    useReloadBillingInfo,
} from "@/queries/organization-queries";
import { computeCoreSubscriptionStatus } from "@/routes/settings/organization/billing/billing-utils";
import { ChangeSubscriptionModal } from "@/routes/settings/organization/billing/ChangeSubscriptionModal";
import { StripePaymentButton } from "@/routes/settings/organization/billing/StripePaymentButton";
import { Timestamp } from "@bufbuild/protobuf";
import { PaymentMethodStatus, SubscriptionStatus } from "gitpod-next-api/gitpod/v1/billing_pb";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { parseAsBoolean, useQueryState } from "nuqs";
import type { FC } from "react";
import { useCallback, useMemo } from "react";

export const CHANGE_PLAN_QUERY_KEY = "change-plan";

export const Plan: FC = () => {
    const { data: organization, isPending: isPendingOrganization } = useOrganization();
    const { data: subscriptions } = useListSubscriptions();
    const { data: billingInfo } = useGetBillingInfo();
    const { reload: reloadBillingInfo } = useReloadBillingInfo();
    const [showChangeModal, setShowChangeModal] = useQueryState(
        CHANGE_PLAN_QUERY_KEY,
        parseAsBoolean.withDefault(false),
    );
    const segmentTrack = useSegmentTrack();
    const tier = organization?.tier;

    const subscription = useMemo(() => {
        if (!subscriptions) {
            return undefined;
        }
        return computeCoreSubscriptionStatus(subscriptions);
    }, [subscriptions]);

    const getTierInfo = (tier: OrganizationTier | undefined): { name: string; description: string } => {
        switch (tier) {
            case OrganizationTier.FREE:
            case OrganizationTier.FREE_ONA:
                return {
                    name: "Free plan",
                    description: "Best for individuals",
                };
            case OrganizationTier.CORE:
                return {
                    name: "Core plan",
                    description: "Ideal for heavy users and small teams",
                };
            case OrganizationTier.ENTERPRISE:
                return {
                    name: "Enterprise plan",
                    description: "Maximum security, compliance, and full control",
                };
            default:
                return {
                    name: "Unknown plan",
                    description: "",
                };
        }
    };

    const tierInfo = getTierInfo(tier);

    const handleSubscriptionModalClose = useCallback(() => {
        reloadBillingInfo();
        void setShowChangeModal(false);
    }, [reloadBillingInfo, setShowChangeModal]);

    const subscriptionStatusPill = useMemo(() => {
        if (tier !== OrganizationTier.CORE) {
            return null;
        }
        switch (subscription?.status) {
            case SubscriptionStatus.ACTIVE:
                return (
                    <Pill variant="success" size="sm">
                        Active
                    </Pill>
                );
            case SubscriptionStatus.CANCELLED:
            case SubscriptionStatus.EXPIRED:
                return (
                    <Pill
                        variant="neutral"
                        tooltip={
                            <div className="flex flex-col gap-1">
                                <Text className="text-content-invert">
                                    Ends on{" "}
                                    {subscription?.endsAt
                                        ? formatShortDate(new Timestamp(subscription?.endsAt).toDate(), true)
                                        : null}
                                </Text>
                            </div>
                        }
                        size="sm"
                    >
                        Cancelled
                    </Pill>
                );
            default:
                return null;
        }
    }, [subscription, tier]);

    const isSubscriptionCancelledWithFutureEndDate = useMemo(() => {
        if (tier !== OrganizationTier.CORE || subscription?.status !== SubscriptionStatus.CANCELLED) {
            return false;
        }
        if (!subscription?.endsAt) {
            return false;
        }
        const endDate = new Timestamp(subscription.endsAt).toDate();
        const now = new Date();
        return endDate > now;
    }, [subscription, tier]);

    const isSubscriptionActiveWithFutureEndDate = useMemo(() => {
        if (tier !== OrganizationTier.CORE || subscription?.status !== SubscriptionStatus.ACTIVE) {
            return false;
        }
        if (!subscription?.endsAt) {
            return false;
        }
        const endDate = new Timestamp(subscription.endsAt).toDate();
        const now = new Date();
        return endDate > now;
    }, [subscription, tier]);

    if (isPendingOrganization) {
        return <LoadingState />;
    }

    return (
        <Card className="space-y-2 bg-surface-glass p-6" variant="bordered">
            <h2 className="mb-4 text-lg font-semibold text-content-primary">Plan</h2>
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex flex-col gap-1">
                    <div className="flex gap-1">
                        <span className="text-base font-medium text-content-primary">{tierInfo.name}</span>
                        {subscriptionStatusPill}
                    </div>
                    {!isSubscriptionCancelledWithFutureEndDate && !isSubscriptionActiveWithFutureEndDate && (
                        <Text className="text-sm text-content-secondary">{tierInfo.description}</Text>
                    )}
                    {isSubscriptionCancelledWithFutureEndDate && subscription?.endsAt && (
                        <Text className="text-sm text-content-secondary">
                            Remains till {formatShortDate(new Timestamp(subscription.endsAt).toDate(), true)}
                        </Text>
                    )}
                    {isSubscriptionActiveWithFutureEndDate && subscription?.endsAt && (
                        <Text className="text-sm text-content-secondary">
                            Ends on {formatShortDate(new Timestamp(subscription.endsAt).toDate(), true)}
                        </Text>
                    )}
                </div>
                <div className="flex flex-wrap gap-2">
                    <StripePaymentButton data-tracking-id="verify-card-plan-card">
                        {({ isLoading, onAddPayment, showForm }) =>
                            !showForm &&
                            billingInfo &&
                            billingInfo.paymentMethodStatus !== PaymentMethodStatus.VERIFIED && (
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        segmentTrack("core_upgrade_started");
                                        onAddPayment();
                                    }}
                                    loading={isLoading}
                                    data-tracking-id="verify-card"
                                >
                                    Verify Card
                                </Button>
                            )
                        }
                    </StripePaymentButton>

                    <Button
                        className="w-fit"
                        disabled={showChangeModal || isSubscriptionCancelledWithFutureEndDate}
                        onClick={() => setShowChangeModal(true)}
                        data-tracking-id="upgrade-plan-card"
                    >
                        Manage
                    </Button>
                    {showChangeModal && <ChangeSubscriptionModal onOpenChange={handleSubscriptionModalClose} />}
                </div>
            </div>
        </Card>
    );
};
